import { AGGREGATE_DATASET_ACTION, aggregateDatasetCard } from "@cleo/shared/aggregateDatasetCard";
import { plainCanvasShareOnly } from "@cleo/shared/canvasShareWords";
import { plainCanvasWrite } from "@cleo/shared/canvasWriteWords";
import { START_CODING_JOB_TOOL, codingJobCard } from "@cleo/shared/codingJobCard";
import { START_WORLD_HELPER_TOOL, worldHelperCard } from "@cleo/shared/worldHelperCard";
import {
  APPLY_COMMERCIAL_CHANGE_TOOL,
  commercialChangeCard,
} from "@cleo/shared/commercialChangeCard";
import {
  APPLY_MEMBER_CHANGE_TOOL,
  MEMBERS_CARD_TOUCH_ID,
  memberChangeCard,
} from "@cleo/shared/memberChangeCard";
import { plainModelAccessCall } from "@cleo/shared/modelAccessWords";

import {
  cloudStepLabel,
  type CloudGateWait,
  type CloudOccurrence,
  type CurrentWait,
  type GateOutcome,
  type WaitingApprovals,
} from "./cloudConversations";

// What a gate-wait shows in the browser's conversation pane and how one decision is made (Can 47's approval clause on
// the web; Pocket's words and law, apps/mobile cloudConversationGate.ts, W-7e4c91a2). Everything a card acts on comes
// from the World's one snapshot of what THIS conversation waits on now (pocketCloudDoors.waitingApprovals: `current`):
// the occurrence, the wait's own event seq, the exact call as the World recorded and verified it, and its approval. A
// card is the live one only when its gate-wait event IS the snapshot's wait; every other card (an older wait, a wait
// whose call is not the current one, history the window did not load) is stale and offers no answer. Nothing is
// joined by a digest, a guess across a gap in the history, or a fallback to "any approval": the details shown, the
// approval decided and the occurrence named are one call.

export interface GateLine {
  readonly label: string;
  readonly value: string;
}

const LABELS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^(to|cc|bcc|recipients?|people|attendees?|participants?|members?|actor|actors)$/iu, "People"],
  [/^(target|url|path|file|id|name|title|subject|resource|channel|world|room)$/iu, "Target"],
  [/^(body|text|content|message|description|html|markdown|prompt)$/iu, "Content"],
  [/^(request_?key|idempotency_?key|client_?key)$/iu, "Request id"],
];

// Every value in full and unambiguous. Text is shown as it is. Any other value is its exact JSON, except that a
// structure holding multi-line text (a Canvas's files) is laid out with each such text as an indented block after "|",
// so its line breaks read as line breaks instead of \n escapes. Single-line strings stay quoted and keys stay quoted,
// so a raw line break can only come from such a block, and a list never looks like a text.
function hasMultiline(value: unknown): boolean {
  if (typeof value === "string") return value.includes("\n");
  if (Array.isArray(value)) return value.some(hasMultiline);
  if (value !== null && typeof value === "object")
    return Object.values(value as Record<string, unknown>).some(hasMultiline);
  return false;
}
function laidOut(value: unknown, indent: string): string {
  const inner = `${indent}  `;
  if (typeof value === "string") {
    if (!value.includes("\n")) return JSON.stringify(value);
    return `|\n${value
      .split("\n")
      .map((line) => `${inner}${line}`)
      .join("\n")}`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    return `[\n${value.map((item) => `${inner}${laidOut(item, inner)}`).join(",\n")}\n${indent}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return "{}";
    return `{\n${entries.map(([key, item]) => `${inner}${JSON.stringify(key)}: ${laidOut(item, inner)}`).join(",\n")}\n${indent}}`;
  }
  return JSON.stringify(value) ?? String(value);
}
function show(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return hasMultiline(value) ? laidOut(value, "") : JSON.stringify(value);
}

/**
 * All of the call's arguments as labelled lines, nothing cut: people, target, content first, the rest by their own
 * names, a request id last.
 */
export function gateCallLines(argumentsJson: string | null): GateLine[] {
  if (!argumentsJson) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(argumentsJson);
  } catch {
    return [{ label: "Details", value: show(argumentsJson) }];
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))
    return [{ label: "Details", value: show(parsed) }];
  const lines: GateLine[] = [];
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    const shown = show(value);
    if (shown.length === 0) continue;
    const known = LABELS.find(([pattern]) => pattern.test(key));
    lines.push({ label: known ? known[1] : key.replace(/_/gu, " "), value: shown });
  }
  const order = ["People", "Target", "Content"];
  const rank = (label: string) => {
    if (label === "Request id") return order.length + 1;
    const index = order.indexOf(label);
    return index === -1 ? order.length : index;
  };
  return lines.sort((a, b) => rank(a.label) - rank(b.label));
}

export type GateCardStatus =
  | "loading"
  | "failed-load"
  | "stale"
  | "cancelled"
  | "awaiting"
  | "deciding"
  | "approved"
  | "declined";

export interface GateCardModel {
  readonly status: GateCardStatus;
  readonly gateInstanceKey: string | null;
  readonly targetDigest: string;
  /** The occurrence of this card, the one a decision names; null unless the card is the World's current wait. */
  readonly occurrence: CloudOccurrence | null;
  readonly title: string;
  /** What Cleo will do: the tool, in the person's words. */
  readonly action: string;
  readonly lines: ReadonlyArray<GateLine>;
  /** When `lines` say the call in words, the exact request, one press away. Empty when `lines` are the exact call. */
  readonly exact: ReadonlyArray<GateLine>;
  /** The exact call's details are the World's own, so the person can read what Approve would allow. */
  readonly detailsReady: boolean;
  /** Opens the Members card for Touch ID; the conversation press decides nothing. */
  readonly approveOnCard?: boolean;
  /** Can 47: the card shown, for an approval approved only on its card; the decision names it (decideGate shownCard). */
  readonly shownCard?: string;
  /** What happens on each answer, in the gate's own terms. */
  readonly effect: string;
  readonly error: string | null;
}

export const STALE_WORDS = "This request is no longer waiting.";
export const CANCELLED_APPROVED_WORDS =
  "Approved, but cancelled and unusable. Cleo stopped before using it; nothing ran.";
export const CANCELLED_WORDS = "Cancelled. Cleo stopped before this was decided; nothing ran.";

/** The snapshot's wait, when this card is that wait. */
export function currentOf(
  wait: Pick<CloudGateWait, "seq">,
  waiting: WaitingApprovals | undefined,
): CurrentWait | null {
  const current = waiting?.current ?? null;
  return current && current.waitSeq === wait.seq ? current : null;
}

type Call = { readonly name: string; readonly argumentsJson: string | null };
const bare = (name: string) => name.replace(/^mcp__[^_]+(?:_[^_]+)*__/u, "");
const parsed = (call: Call): Record<string, unknown> | null => {
  if (!call.argumentsJson) return null;
  try {
    const args: unknown = JSON.parse(call.argumentsJson);
    return args !== null && typeof args === "object" && !Array.isArray(args)
      ? (args as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/** A Canvas call is titled with the Canvas it makes or changes, not the gate's general class ("your own records"). */
function canvasTitle(call: Call | null): string | null {
  const args = call && bare(call.name) === "neucleos_canvas" ? parsed(call) : null;
  if (!args) return null;
  const verb = args.action === "create" ? "Make" : args.action === "save" ? "Change" : null;
  if (verb === null) return null;
  // The title is model-written: one line, no control or format characters, no quote marks that could fake the
  // closing quote, at most 60 characters. The full value is still in the lines below.
  const raw = typeof args.title === "string" ? args.title : "";
  const clean = raw
    .replace(/[\p{Cc}\p{Cf}\s]+/gu, " ")
    .replace(/[“”„‟"]/gu, "")
    .trim();
  const title = clean.length > 60 ? `${clean.slice(0, 59).trimEnd()}…` : clean;
  return title ? `${verb} the Canvas “${title}”` : `${verb} a Canvas`;
}

type Words = { readonly title: string; readonly lines: GateLine[] };
const rows = (headline: string, pairs: ReadonlyArray<readonly [string, string]>): Words => ({
  title: headline,
  lines: pairs.map(([label, value]) => ({ label, value })),
});

/** A Canvas share in the person's words (the Desk Inbox's own, @cleo/shared/canvasShareWords); null for any other call. */
function plainShare(call: Call | null, names: CurrentWait["names"]): Words | null {
  const args = call && bare(call.name) === "neucleos_canvas" ? parsed(call) : null;
  if (!args || args.action !== "canvas-share") return null;
  const words = plainCanvasShareOnly(args, {
    canvasName: names?.canvasName ?? null,
    recipient: names?.recipient ?? null,
    shareData: names?.shareData ?? null,
  });
  return words ? rows(words.headline, words.rows) : null;
}

/**
 * Can 47: the card an approval approved only on its card needs, when this pane shows it exactly as the Desk does (the
 * same words, @cleo/shared). Null when the World names no such card, or the call is not one the card can show exactly
 * (then only Decline is offered).
 */
function cardOnlyCard(
  call: Call,
  card: string | undefined,
  memberChange: unknown,
  commercialChange: unknown,
): (Words & { readonly card: string | null; readonly approveOnCard?: boolean }) | null {
  const args = card ? parsed(call) : null;
  if (!card || !args) return null;
  const tool = bare(call.name);
  const built =
    card === "coding-job-card-v1" && tool === START_CODING_JOB_TOOL
      ? codingJobCard(args)
      : card === "aggregate-dataset-card-v1" &&
          tool === "neucleos_canvas" &&
          args.action === AGGREGATE_DATASET_ACTION
        ? aggregateDatasetCard(args)
        : card === "world-members-card-v1" && tool === APPLY_MEMBER_CHANGE_TOOL
          ? memberChangeCard(args, memberChange, Date.now())
          : card === "commercial-change-card-v1" && tool === APPLY_COMMERCIAL_CHANGE_TOOL
            ? commercialChangeCard(args, commercialChange, Date.now())
            : card === "world-helper-card-v1" && tool === START_WORLD_HELPER_TOOL
              ? worldHelperCard(args)
              : null;
  if (!built) return null;
  const words = rows(built.headline, built.rows);
  // A Members or price card that cannot be approved says why, as the Desk's does (card null: Approve stays off).
  if (built.blocked !== null)
    return card === "world-members-card-v1" || card === "commercial-change-card-v1"
      ? {
          ...words,
          card: null,
          ...(card === "world-members-card-v1" &&
          "approveOnCard" in built &&
          built.approveOnCard === true
            ? { approveOnCard: true }
            : {}),
        }
      : null;
  return { ...words, card };
}

/** A Canvas create or save, in words (@cleo/shared/canvasWriteWords); null for any other call. */
function plainCanvasWriteOf(call: Call): Words | null {
  const args = bare(call.name) === "neucleos_canvas" ? parsed(call) : null;
  if (!args) return null;
  const words = plainCanvasWrite(args);
  return words ? rows(words.headline, words.rows) : null;
}

/** A call these words describe from the World's card facts, in words; null for any other. */
function plainKnownCall(call: Call, facts: unknown): Words | null {
  const args = parsed(call);
  if (!args) return null;
  const words = plainModelAccessCall(bare(call.name), args, facts);
  return words ? rows(words.headline, words.rows) : null;
}

export function gateCardModel(input: {
  wait: CloudGateWait;
  /** The World's snapshot; undefined until it has answered. */
  waiting: WaitingApprovals | undefined;
  /** The snapshot could not be had: Try again is offered, no decision is. */
  loadFailed?: boolean;
  /** The World refused this card's decision as not waiting: it is stale now, whatever the snapshot has not yet said. */
  refusedStale?: boolean;
  deciding: boolean;
  error: string | null;
}): GateCardModel {
  const { wait, waiting } = input;
  const current = currentOf(wait, waiting);
  const approval = current?.approval ?? null;
  let status: GateCardStatus;
  if (waiting === undefined) status = input.loadFailed ? "failed-load" : "loading";
  else if (!current || !approval || input.refusedStale) status = "stale";
  else if (approval.invalidatedAt !== null && approval.invalidatedAt !== undefined)
    status = "cancelled";
  else if (approval.state === "CREATED") status = input.deciding ? "deciding" : "awaiting";
  else status = approval.outcome === "declined" ? "declined" : "approved";
  const verified = status !== "loading" && status !== "failed-load" && status !== "stale";
  const call = verified && !input.refusedStale && current ? current.call : null;
  const cancelledApproved =
    status === "cancelled" && approval?.state === "DECIDED" && approval.outcome !== "declined";
  const shownCard =
    call && approval?.cardApproved === true
      ? cardOnlyCard(call, approval.card, approval.memberChange, approval.commercialChange)
      : null;
  const plainCall =
    shownCard ??
    (call ? (plainKnownCall(call, approval?.cardFacts) ?? plainCanvasWriteOf(call)) : null);
  const share = plainCall ?? plainShare(call, current?.names);
  const action = cloudStepLabel(call?.name ?? wait.name, call?.argumentsJson ?? wait.argumentsJson);
  return {
    status,
    gateInstanceKey: approval?.gateInstanceKey ?? null,
    targetDigest: approval?.targetDigest ?? wait.digest,
    occurrence: current && approval && !input.refusedStale ? current.occurrence : null,
    title: share?.title ?? canvasTitle(call) ?? approval?.title ?? `Cleo wants to ${action}`,
    action: share && !plainCall ? "share a Canvas" : action,
    lines: share ? share.lines : call ? gateCallLines(call.argumentsJson) : [],
    exact: share && call ? gateCallLines(call.argumentsJson) : [],
    // A gate approved only on its card is approvable here only on a card this pane shows exactly; otherwise Decline only.
    detailsReady:
      call !== null &&
      (approval?.cardApproved !== true || (shownCard !== null && shownCard.card !== null)),
    ...(shownCard?.card ? { shownCard: shownCard.card } : {}),
    ...(status === "awaiting" && shownCard?.approveOnCard ? { approveOnCard: true } : {}),
    effect:
      status === "approved"
        ? "Approved. Cleo is continuing with exactly this action, once."
        : status === "declined"
          ? "Declined. Cleo was told and will not do this."
          : status === "cancelled"
            ? cancelledApproved
              ? CANCELLED_APPROVED_WORDS
              : CANCELLED_WORDS
            : status === "stale"
              ? STALE_WORDS
              : status === "failed-load"
                ? "Cleo could not check this approval. Try again."
                : status === "loading"
                  ? "Checking exactly what Cleo is waiting on."
                  : shownCard?.approveOnCard
                    ? MEMBERS_CARD_TOUCH_ID
                    : "Approve lets Cleo do exactly this once. Decline tells her not to.",
    error: input.error,
  };
}

/**
 * One decision per card. The first Approve or Decline is sent; any press after it, either way, is not sent again, and
 * a decision the World refused can be tried again. The decision key is fixed per instance and outcome, and is Pocket's
 * own for the same approval (createGateDecider there), so a decision made on both screens is one decision, replayed,
 * never a second.
 */
export function createGateDecider(options: {
  gateInstanceKey: string;
  targetDigest: string;
  occurrence: { turnKey: string; step: number; toolRunId: string };
  shownCard?: string;
  send: (input: {
    gateInstanceKey: string;
    targetDigest: string;
    outcome: GateOutcome;
    decisionKey: string;
    occurrence: { turnKey: string; step: number; toolRunId: string };
    shownCard?: string;
  }) => Promise<unknown>;
  onError?: (cause: unknown) => void;
}) {
  let inFlight = false;
  let decided = false;
  return {
    get decided() {
      return decided;
    },
    async decide(outcome: GateOutcome): Promise<"sent" | "ignored" | "failed"> {
      if (inFlight || decided) return "ignored";
      inFlight = true;
      try {
        await options.send({
          gateInstanceKey: options.gateInstanceKey,
          targetDigest: options.targetDigest,
          outcome,
          decisionKey: `pocket-cloud:${options.gateInstanceKey}:${outcome}`,
          occurrence: options.occurrence,
          ...(options.shownCard ? { shownCard: options.shownCard } : {}),
        });
        decided = true;
        return "sent";
      } catch (cause) {
        options.onError?.(cause);
        return "failed";
      } finally {
        inFlight = false;
      }
    },
  };
}
